import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import Api from '@services/api';
import ClearButton from './clear-button';
import Loader from './loader';
import Results from './results';
import { TextInput } from 'nhsuk-react-components';
import styles from './hcpc.module.scss';
import { validateHcpcRecord } from './hcpc.utils';

const Hcpc = forwardRef((props, ref) => {
    const inputRef = useRef(null);

    // Stores the active debounce timer for HCPC searches.
    const debounceRef = useRef(null);

    // Tracks the latest HCPC search so stale API responses can be ignored.
    const latestSearchRef = useRef(0);

    const [data, setData] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [error, setError] = useState(undefined);
    const [focused, setFocused] = useState(-1);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [resultsVisible, setResultsVisible] = useState(false);

    const [selectedRecord, setSelectedRecord] = useState(null);
    const [acceptedRecord, setAcceptedRecord] = useState(null);
    const [validationReason, setValidationReason] = useState(null);

    const GMC_HCPC_ERROR = 'You cannot enter an HCPC PIN when a GMC PIN has been entered.';

    useEffect(() => {
        window.addEventListener('click', handleWindowClicked);
        setIsReadOnly(props.question.isReadOnly);

        return () => {
            window.removeEventListener('click', handleWindowClicked);

            // Cleanup pending searches when the component unmounts.
            clearTimeout(debounceRef.current);
            latestSearchRef.current += 1;
        };
    }, [props.question.isReadOnly]);

    useImperativeHandle(ref, () => ({
        validate,
        isValid,
        acceptedRecord,
        validationReason
    }));

    // Restore stored HCPC validation state when the question state changes.
    useEffect(() => {
        const currentValue = inputRef.current?.value ?? '';

        if (
            currentValue === props.question.value &&
            validationReason === props.question.validationReason &&
            acceptedRecord === props.question.acceptedRecord
        ) {
            return;
        }
        if (typeof props.question.acceptedRecord !== 'undefined') {
            setSelectedRecord(props.question.acceptedRecord);
            setAcceptedRecord(props.question.acceptedRecord);
        }

        if (typeof props.question.validationReason !== 'undefined') {
            setValidationReason(props.question.validationReason);
        }

        const hasAcceptedRecord = !!props.question.acceptedRecord;

        setError(prevError =>
            hasAcceptedRecord ? props.question.error : props.question.error ?? prevError
        );

        setIsValid(prevIsValid =>
            hasAcceptedRecord
                ? true
                : typeof props.question.isValid === 'undefined'
                ? prevIsValid
                : !!props.question.isValid
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        props.question.acceptedRecord,
        props.question.validationReason,
        props.question.error,
        props.question.isValid
    ]);

    // HCPC cannot be entered when a GMC value already exists.
    const hasGmcValue = () => {
        return !!String(props.gmcValue ?? '').trim();
    };

    // Validate HCPC input, selected record state, and GMC conflict.
    const validate = (requireAccepted = true, valueOverride) => {
        const value = String(valueOverride ?? props.question.value ?? '').trim();
        const hasValue = value.length > 0;

        if (!hasValue) {
            setValidationError(undefined, true);
            return true;
        }

        if (hasGmcValue()) {
            setValidationError(GMC_HCPC_ERROR, false);
            return false;
        }

        const restoredAcceptedRecord = acceptedRecord || props.question.acceptedRecord;
        const restoredValidationReason = validationReason || props.question.validationReason;
        const hasPreviouslyAcceptedValue = isReadOnly && hasValue;

        if (requireAccepted && !restoredAcceptedRecord && !hasPreviouslyAcceptedValue) {
            setValidationError(
                restoredValidationReason || 'Select a valid HCPC record from the list',
                false
            );
            return false;
        }

        setValidationError(undefined, true);
        return true;
    };

    // Persist validation state locally and on the question object.
    const setValidationError = (error, isValid) => {
        setError(error);
        setIsValid(isValid);

        props.question.error = error;
        props.question.isValid = isValid;
    };

    // Store HCPC validation metadata on the question so it survives parent re-renders.
    const updateHcpcQuestionState = ({ acceptedRecord = null, validationReason = null }) => {
        props.question.acceptedRecord = acceptedRecord;
        props.question.validationReason = validationReason;
    };

    // Reset accepted HCPC state when the user changes the typed value.
    const handleOnInputChange = async e => {
        let { value } = e.target;

        value = value
            .replace(/[^a-zA-Z0-9 ]/g, '')
            .replace(/\s+/g, ' ')
            .trimStart()
            .toUpperCase();

        if (value !== props.question.value) {
            setSelectedRecord(null);
            setAcceptedRecord(null);

            const errorMessage =
                value.length >= 3 ? 'Select a valid HCPC record from the list' : null;

            setValidationReason(errorMessage ?? null);

            updateHcpcQuestionState({
                acceptedRecord: null,
                validationReason: errorMessage ?? null
            });

            setValidationError(errorMessage, false);

            handleResultsVisibility(value.length);

            await props.onChange({
                questionId: props.question.id,
                value
            });
        }
    };

    const handleOnInputFocus = e => {
        handleResultsVisibility(e.target.value.length);
    };

    // Show HCPC results only when input is active, long enough, and editable.
    const handleResultsVisibility = count => {
        const minCount = 3;
        const isInputActive = inputRef.current === document.activeElement;

        if (hasGmcValue()) {
            clearTimeout(debounceRef.current);
            setResultsVisible(false);
            setData([]);
            return;
        }

        if (count >= minCount && isInputActive && !isReadOnly) {
            setResultsVisible(true);

            clearTimeout(debounceRef.current);

            // Debounce HCPC API searches to avoid searching on every key press.
            debounceRef.current = setTimeout(() => {
                search();
            }, 400);
        } else {
            setResultsVisible(false);
        }
    };

    const updateInputValue = async (questionId, value) => {
        await props.onChange({
            questionId,
            value
        });
    };
    // Normalise HCPC values for duplicate-safe comparison.
    const normaliseHcpcValue = value =>
        String(value ?? '')
            .trim()
            .replace(/\s+/g, ' ')
            .toUpperCase();

    // Build a stable key for identifying duplicate HCPC records.
    const getHcpcResultKey = record =>
        [record?.Registration_Name, record?.First_Name, record?.Last_Name, record?.Gender]
            .map(normaliseHcpcValue)
            .join('|');

    // Remove duplicate HCPC records before rendering results.
    const dedupeHcpcResults = records => {
        const seen = new Set();

        return records.filter(record => {
            const key = getHcpcResultKey(record);

            if (seen.has(key)) {
                return false;
            }

            seen.add(key);
            return true;
        });
    };

    // Search HCPC records and ignore stale responses from older searches.
    const search = async () => {
        const searchValue = inputRef.current?.value ?? '';

        // Prevent short values from triggering HCPC searches.
        if (searchValue.length < 3) {
            setData([]);
            setDataLoading(false);
            setResultsVisible(false);
            return;
        }

        const searchId = latestSearchRef.current + 1;
        latestSearchRef.current = searchId;

        setData([]);
        setDataLoading(true);

        try {
            const api = new Api();
            const response = await api.getHCPCRefData(searchValue);

            // Ignore this response if a newer HCPC search has already started.
            if (searchId !== latestSearchRef.current) {
                return;
            }

            if (response.data) {
                setData(dedupeHcpcResults(response.data));
                setFocused(-1);
            }
        } catch {
            // Store HCPC validation metadata on the question so it survives parent re-renders.
            if (searchId === latestSearchRef.current) {
                setFocused(-1);
            }
        } finally {
            if (searchId === latestSearchRef.current) {
                setDataLoading(false);
            }
        }
    };

    // Select the clicked result, or the focused result for keyboard selection.
    const handleUpdateResults = async selectedIndex => {
        const index = typeof selectedIndex === 'number' ? selectedIndex : focused;
        const record = data[index];

        if (!record) {
            const message = 'Select a valid HCPC record from the list';

            setAcceptedRecord(null);
            setValidationReason(message);
            setValidationError(message, false);

            updateHcpcQuestionState({
                acceptedRecord: null,
                validationReason: message
            });

            return;
        }

        if (hasGmcValue()) {
            setAcceptedRecord(null);
            setValidationReason(GMC_HCPC_ERROR);

            updateHcpcQuestionState({
                acceptedRecord: null,
                validationReason: GMC_HCPC_ERROR
            });

            setValidationError(GMC_HCPC_ERROR, false);
            return;
        }

        setSelectedRecord(record);
        populateLinkedFields(record);
        setData([]);

        const result = validateHcpcRecord(record, props.enteredDetails || {}, {
            skipHcpcPersonValidation: props.skipHcpcPersonValidation
        });

        if (result.accepted) {
            setAcceptedRecord(record);
            setValidationReason(null);
            setValidationError(undefined, true);

            updateHcpcQuestionState({
                acceptedRecord: record,
                validationReason: null
            });
        } else {
            setAcceptedRecord(null);
            setValidationReason(result.reason);
            setValidationError(result.reason, false);

            updateHcpcQuestionState({
                acceptedRecord: null,
                validationReason: result.reason
            });
        }
    };

    // Populate linked fields from the selected HCPC reference record.
    const populateLinkedFields = refData => {
        const linkedQuestions = props.question.linked_questions;

        for (const questionId in linkedQuestions) {
            updateInputValue(linkedQuestions[questionId], refData?.[questionId]);
        }

        updateInputValue(props.question.id, refData?.Registration_Name);

        if (props.handleDisable) {
            props.handleDisable({
                isReadOnly: !!refData?.Registration_Name,
                questionIds: [
                    props.question.id,
                    ...(linkedQuestions ? Object.values(linkedQuestions) : [])
                ]
            });
        }
    };

    // Clear HCPC input, results, validation state, and pending searches.
    const clearInputValue = e => {
        e.preventDefault();

        clearTimeout(debounceRef.current);
        latestSearchRef.current += 1;
        setData([]);
        setResultsVisible(false);
        setFocused(-1);
        setDataLoading(false);

        setSelectedRecord(null);
        setAcceptedRecord(null);
        setValidationReason(null);

        updateHcpcQuestionState({
            acceptedRecord: null,
            validationReason: null
        });

        updateInputValue(props.question.id, '');

        if (props.handleDisable) {
            props.handleDisable({
                isReadOnly: false,
                questionIds: [
                    props.question.id,
                    ...(props.question.linked_questions
                        ? Object.values(props.question.linked_questions)
                        : [])
                ]
            });
        }
    };

    // Support keyboard navigation through HCPC search results.
    const handleOnKeyDown = e => {
        const numFocusableItems = data.length - 1;

        // eslint-disable-next-line default-case
        switch (e.key) {
            case 'ArrowDown':
                if (focused < numFocusableItems) {
                    const newFocus = focused + 1;
                    setFocused(newFocus);
                    handleResultsScroll(newFocus);
                }
                break;
            case 'ArrowUp':
                if (focused > 0) {
                    const newFocus = focused - 1;
                    setFocused(newFocus);
                    handleResultsScroll(newFocus);
                }
                break;
            case 'Escape':
                inputRef.current.blur();
                setResultsVisible(false);
                break;
            case 'Enter':
                e.preventDefault();
                handleUpdateResults();
                break;
        }
    };

    // Keep the focused dropdown result visible while navigating.
    const handleResultsScroll = focused => {
        const results = document.querySelector('#results');
        const item = document.querySelector('#results > li');
        if (results && item) {
            results.scrollTo({
                behavior: 'smooth',
                top: focused * item.getBoundingClientRect().height
            });
        }
    };

    // Hide HCPC results when clicking outside the input.
    const handleWindowClicked = e => {
        if (e.target.type !== 'text') {
            setResultsVisible(false);
        }
    };

    // Revalidate the selected HCPC record when person details change.
    useEffect(() => {
        if (!selectedRecord) return;

        const result = validateHcpcRecord(selectedRecord, props.enteredDetails || {}, {
            skipHcpcPersonValidation: props.skipHcpcPersonValidation
        });

        if (result.accepted) {
            setAcceptedRecord(selectedRecord);
            setValidationReason(null);
            setValidationError(undefined, true);

            updateHcpcQuestionState({
                acceptedRecord: selectedRecord,
                validationReason: null
            });
        } else {
            setAcceptedRecord(null);
            setValidationReason(result.reason);
            setValidationError(result.reason, false);

            updateHcpcQuestionState({
                acceptedRecord: null,
                validationReason: result.reason
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        props.enteredDetails?.firstName,
        props.enteredDetails?.surname,
        props.enteredDetails?.gender,
        props.skipHcpcPersonValidation
    ]);

    // Revalidate HCPC when GMC changes to keep mutual exclusion in sync.
    useEffect(() => {
        const hasHcpcValue = !!String(props.question.value ?? '').trim();
        const hasQuestionError = !!props.question.error;

        if (hasHcpcValue || hasQuestionError) {
            validate(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.gmcValue]);

    const value = props.question.value ?? '';
    const hasHidden = props.question.hidden ?? true;

    return !hasHidden || (hasHidden && !props.question.hidden) ? (
        <div className={`nhsuk-u-clear ${styles.SearchWithResults}`}>
            <TextInput
                className={styles.SearchWithResultsInput}
                disabled={isReadOnly}
                error={error}
                hint={props.question.hint_text}
                id={props.id}
                inputRef={inputRef}
                label={props.question.name}
                onChange={handleOnInputChange}
                onFocus={handleOnInputFocus}
                onKeyDown={handleOnKeyDown}
                value={value}
                width="10"
                required={props.question.mandatory}
            />
            <Results
                data={data}
                focused={focused}
                handleClick={handleUpdateResults}
                setFocused={setFocused}
                visible={resultsVisible}
            />
            <ClearButton handleClick={clearInputValue} visible={isReadOnly} />
            <Loader loading={dataLoading} />
        </div>
    ) : null;
});

export default Hcpc;
